import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useAuth } from "../providers/AuthProvider";
import { hasPermission, hasAnyPermission } from "../permissions";
import { FullPageSpinner } from "../../components/feedback/FullPageSpinner";

/**
 * Emits a translated toast then redirects.
 * Split into its own component so the `useEffect` hook can fire
 * without violating the rules-of-hooks in the guard below.
 */
function AccessDeniedRedirect({ to = "/dashboard" }) {
  const { t } = useTranslation();
  useEffect(() => {
    toast.error(t("admin.access_denied_toast"), {
      id: "access-denied",
      // Suppress duplicate toasts on rapid re-mounts (e.g. React 19
      // StrictMode dev double-invocation).
    });
  }, [t]);
  return <Navigate to={to} replace />;
}

/**
 * Guards a route on:
 *   - authentication status (redirects to /login when anonymous)
 *   - optional permission (redirects to /dashboard with a translated
 *     toast when the user is authenticated but not authorised)
 */
export function ProtectedRoute({ children, permission }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === "loading") return <FullPageSpinner />;

  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (permission) {
    const ok = Array.isArray(permission)
      ? hasAnyPermission(user, permission)
      : hasPermission(user, permission);
    if (!ok) return <AccessDeniedRedirect />;
  }

  return children;
}
